import {Location} from '@angular/common';
import {HttpClient, HttpErrorResponse, HttpEventType} from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {CreatorSection, defaultTags, ModelTag} from '../../models/CreatorModels';
import {AuthService} from '../../services/auth.service';
import {CommonService} from '../../services/common.service';
import {ConsentsFormService} from '../../services/consents-form.service';
import {FileService} from '../../services/file.service';
import {LocalStorageService} from '../../services/local-storage.service';
import {NotificationService} from '../../services/notification.service';
import {ToastMessagesService} from '../../services/toast-messages.service';

@Component({
  selector: 'app-public-form-display',
  templateUrl: './public-form-display.component.html',
  styleUrls: ['./public-form-display.component.scss']
})
export class PublicFormDisplayComponent implements OnInit {
  //#region class members
  parent: any;
  formId: number;
  user: any;
  parentDisplayName: string;
  code = '';
  edit: boolean;
  form: any;
  isLoading: boolean;
  mode: string;
  sectionTypes: any[];
  sections: any[] = [];
  showTwoFactor: boolean;
  showUploadField: boolean;
  uniqueId: string;
  isFieldDisabled: boolean;
  isExternal: boolean;
  defaultMemberTags: ModelTag[] = defaultTags;
  editorStylePreview: any;
  update: boolean;
  files: any[] = [];
  filesPath: any;
  currentFiles: any[] = [];
  isChecked: boolean;
  checkParentId: number;
  //#endregion

  constructor(public route: ActivatedRoute,
              private commonService: CommonService,
              public router: Router,
              private httpClient: HttpClient,
              public location: Location,
              private authService: AuthService,
              private fileService: FileService,
              private localStorageService: LocalStorageService,
              private notificationService: NotificationService,
              public toastMessagesService: ToastMessagesService,
              private consentFormService: ConsentsFormService) {
    this.user = JSON.parse(this.localStorageService.get('user'));
    this.isLoading = true;
    this.sectionTypes = this.commonService.sectionTypes;
    this.parentDisplayName = '';
    this.showTwoFactor = false;
    this.showUploadField = false;
    this.isFieldDisabled = false;
    this.editorStylePreview = {height: '100%;', border: '0px'};
    this.isChecked = false;

  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(async (params) => {
      this.uniqueId = params.unique_id;
      this.isExternal = params.isExternal ? params.isExternal === 'true' : false;
      this.formId = params.id ? +params.id : null;
      this.checkParentId = params.checkParentId ? +params.checkParentId : null;
      console.log(this.user.id);
      await this.initializeParent(this.checkParentId ? this.checkParentId : this.user.id);
      if (this.isExternal) {
        const shouldInitializeNewForm = await this.initializeCompletedForm(this.user.id, this.uniqueId);
        if (shouldInitializeNewForm) {
          this.mode = 'false';
          this.edit = false;
          await this.initializeForm(this.uniqueId);
        } else {
          this.mode = 'false';
          this.edit = true;
          this.isFieldDisabled = this.form.canParentEdit === 0;
        }
        this.isLoading = false;
      } else {
        this.mode = 'false';
        this.edit = false;
        if (this.formId) {
          const shouldInitFreshForm =  await this.initializeCompletedForm(this.parent.id, this.uniqueId, this.formId);
          if (shouldInitFreshForm) {
            await this.initializeForm(this.uniqueId);
          } else {
            this.isFieldDisabled = this.form.canParentEdit === 0;
          }
        } else {
          // First init
          await this.initializeForm(this.uniqueId);
        }
        this.isLoading = false;
      }
      console.log(this.form);
      this.isChecked = this.form.isChecked && this.form.isChecked === 1 ? true : false;

      this.showUploadField = this.form.hasUploadField ? this.form.hasUploadField : false;
      if (this.uniqueId && this.parent.id) {
        await this.initializeUploadedFiles(this.form.parent_id);
      }
    });
  }

  async initializeUploadedFiles(id?: number) {
    try {
      const parentId = id ? id : this.parent.id;
      const request = await this.commonService.getList(`files/upload-custom-form-files-parent?uniqueId=${this.uniqueId}&parentId=${parentId}`);
      this.currentFiles = request.object;
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.message);
    }
  }

  async initializeParent(id: number) {
    try {
      const result = await this.commonService.getList(`people/parent?id=${id}`);
      this.parent = result.object[0];
      this.parent.displayName = `${this.parent.first_name} ${this.parent.last_name}`;
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.message);
    }
  }

  async initializeForm(uniqueId: string, id?: number) {
    try {
      this.form = await this.consentFormService.getCustomFormByUniqueId(uniqueId);
      this.sections = JSON.parse(this.form.sections);

      for (const section of this.sections) {
        section.expanded = false;
        section.title = section.title ? section.title.replace(/'/g, '\'\'') : '';
        for (const member of section.members) {
          if (member.type.type === 'label') {
            if (member.value) {
              member.value = this.getEditorMemberPredefinedValue(member.value);
            }
          }

          if (member.type.type === 'toggle') {
            member.value = false;
          }

          if (member.tag) {
            member.value = this.getMemberPredefinedValue(member.tag);
          }
          member.title = member.title ? member.title.replace(/'/g, '\'\'') : '';
          const type = this.sectionTypes.find(t => t.id === member.type.id);
          if (type) {
            member.type = type;
          }
        }
      }
    } catch (e) {
      console.log(e);
      this.toastMessagesService.toastErrorMessage('Δεν μπορέσαμε να αρχικοποιήσουμε την δήλωση');
    } finally {
      this.isLoading = false;
    }
  }

  // initializeCompletedForm
  async initializeCompletedForm(parentId, uniqueId, id?: number) {
    try {
      this.form = id ?  await this.consentFormService.getCustomCompletedFormParentResults(parentId, uniqueId, undefined, id) :  await this.consentFormService.getCustomCompletedFormParentResults(parentId, uniqueId);
      if (this.form) {
        // this.isFieldDisabled = this.form.canPareEdit === 1;
        const value = JSON.parse(this.form.value);
        this.sections = value.results;
        for (const section of this.sections) {
          section.expanded = false;
          section.title = section.title ? section.title.replace(/'/g, '\'\'') : '';
          for (const member of section.members) {
            member.title = member.title ? member.title.replace(/'/g, '\'\'') : '';
            const type = this.sectionTypes.find(t => t.id === member.type.id);
            if (type) {
              member.type = type;
            }
          }
        }
        for (const section of value.results) {
          for (const member of section.members) {
            if (member.value) {
              const val = member.options.find(option => option.id === member.value.id);
              if (val) {
                member.value = val;
              }
            }
          }
        }
        // this.isFieldDisabled = true;
        return false;
      } else {
        return true;
      }
    } catch (e) {
      console.log(e);
      // this.toastMessagesService.toastErrorMessage('Δεν μπορέσαμε να αρχικοποιήσουμε την δήλωση');
      return true;
    }
  }


  checkMandatory(sections: CreatorSection[]) {
    for (const section of this.sections) {
      for (const member of section.members) {
        if (member.mandatory) {
          if (member.type.type !== 'label' && member.type.type !== 'image' && member.type.type !== 'checkbox') {
            if (member.value === null || member.value === false) {
              return false;
            }
          }

          if (member.type.type === 'checkbox') {
            if (member.value.length === 0) {
              return false;
            }
          }
        }
      }
    }

    return true;
  }

  checkChanged(section: any, member: any, option: any) {
    if (option.value) {
      if (option.value === 1) {
        option.value = 0;
      } else {
        option.value = 1;
      }
    } else {
      option.value = 1;
    }
  }


  //#region Two step functions
  async requestCode() {
    try {
      // tslint:disable-next-line:max-line-length
      const response: any = await this.commonService.updateItem(`twoStep/customForms`, {
        // childId: this.child.id,
        formUniqueId: this.uniqueId,
      });
      this.toastMessagesService.toastMessages(response.object.message);
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.message);
    }
  }

  async validateCode() {
    try {
      const response = await this.submitTwoFactorCode();
      if (response && response.status === 200) {
        await this.finalizeSubmission();
      } else {
        this.toastMessagesService.toastErrorMessage('Ο κωδικός που πληκτρολογήσατε δεν είναι σωστός');
      }
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(`Ο κωδικός που πληκτρολογήσατε δεν είναι σωστός`);
    } finally {

    }
  }

  // Submitting inserted two factor code
  async submitTwoFactorCode() {
    try {
      const response: any = await this.commonService.insertItem(`twoStep/customForms/authStep`, {
        securityNumber: this.code,
        // childId: this.child.id,
        formUniqueId: this.uniqueId
      });
      return response;
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.message);
    }
  }

  //#endregion

  //#region upload and display files
  onFileInput($event: any) {
    const tempFiles = $event.target.files;
    for (const file of tempFiles) {
      file.status = false;
      file.progress = 0;
      this.files.push(file);
    }
  }

  removeFile(index: number) {
    this.files.splice(index, 1);
  }

  uploadFiles() {
    return new Promise(async (resolve, reject) => {
      for (const file of this.files) {
        try {
          if (!file.status) {
            await this.uploadFile(file);
          }
        } catch (e) {
          this.toastMessagesService.toastErrorMessage(e);
          reject(e);
        }
      }
      console.log('done');
      resolve();
    });
  }


  uploadFile(file) {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);
      const model = {
        uniqueId: this.uniqueId,
        parentId: this.parent ? this.parent.id : null,
      };
      file.inProgress = true;
      this.fileService.customParentUpload(formData, model).pipe(
          map(event => {
            switch (event.type) {
              case HttpEventType.UploadProgress:
                file.progress = Math.round(event.loaded * 100 / event.total);
                if (file.progress === 100) {
                  file.status = true;
                }
                break;
              case HttpEventType.Response:
                return event;
            }
          }),
          catchError((error: HttpErrorResponse) => {
            file.inProgress = false;
            this.toastMessagesService.toastErrorMessage(`Η αποθήκυεση του ${file.name} απέτυχε`);
            reject(error);
            return of(`Upload failed: ${file.name}`);
          })).subscribe((event: any) => {
        if (typeof (event) === 'object') {
          if (event.body.storagePath) {
            this.filesPath = event.body.storagePath;
            resolve();
          }
        }
      });
    });
  }

  async displayAttachment(file: any) {
    try {
      const image = await this.fileService.getFile(`files/serveFile`, file.path);
      let type = '';
      if (file.originalname.toLowerCase().indexOf('.png')) {
        type = 'image/png';
      }
      if (file.originalname.toLowerCase().indexOf('.jpg')) {
        type = 'image/jpg';
      }
      if (file.originalname.toLowerCase().indexOf('.jpeg')) {
        type = 'image/jpeg';
      }
      if (file.originalname.toLowerCase().indexOf('.pdf')) {
        type = 'application/pdf';
      }

      const finalImage = await this.blobToImage(image);
      const newTab = window.open('about:blank', '_blank');
      newTab.document.write(`<object style="margin-left: auto; margin-right:auto;
  width: 50%;
  height: 100%;
  padding: 10px;" data=${finalImage} type="${type}"></object>`);
      newTab.document.close();
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e);
    }
  }

  blobToImage = (blob) => {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result;
        resolve(base64data);
      };
    });
  }
  //#endregion

  //#region bind values replacement
  fieldHasTag(member: any) {
    return !!member.tag;
  }

  getMemberPredefinedValue(tag: ModelTag) {
    try {
      if (tag.value === '[parent-displayName]') {
        return this.parentDisplayName ? this.parentDisplayName : '-';
      }
    } catch (e) {
      console.log(e);
    }
  }

  getEditorMemberPredefinedValue(value) {
    try {
      value = value.replace('[parent-displayName]', `${this.parent.displayName}`);
    } catch (e) {
      console.log(e);
    }
    return value;
  }

  displayButton() {
    if (this.form?.has_deadline !== 1) {
      return true;
    } else {
      const deadline = new Date(this.form.deadline_date);
      if(deadline >= new Date()) {
        return true;
      } else {
        return false;
      }
    }
  }

  //#endregion
  async submitRequest() {
    // if (this.form.has_two_factor === 1) {
    //     this.showTwoFactor = true;
    //     await this.requestCode();
    // } else {
    await this.finalizeSubmission();
    // }
    // try {
    //     await this.finalizeSubmission();
    // } catch (e) {
    //     this.toastMessagesService.toastErrorMessage(e);
    // }
  }

  async updateRequest() {
    try {
      await this.finalizeSubmission();
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e);
    }
  }

  async finalizeSubmission() {
    this.isLoading = true;
    for (const section of this.sections) {
      for (const member of section.members) {
        if (member.type.type === 'checkbox') {
          member.value = member.options.filter(o => o.value === 1);
        }
      }
    }
    try {
      const model = {
        formUniqueId: this.uniqueId,
        results: JSON.stringify({
          results: this.sections
        })
      };


      if (!this.checkMandatory(this.sections)) {
        this.toastMessagesService.toastMessages('Παρακαλούμε συμπληρώστε όλα τα υποχρεωτικά πεδία που έχουν *');
        return;
      }

      // upload files if exist
      // if (this.files.length > 0) {
      //     await this.uploadFiles();
      // }

      if (this.showUploadField && this.files.length > 0) {
        await this.uploadFiles();
      } else if (this.showUploadField && this.files.length === 0 && this.currentFiles.length === 0) {
        this.toastMessagesService.toastMessages('Θα πρέπει να μεταφορτώσετε τα ζητούμενα αρχεία');
        return;
      }

      // console.log(this.sections);
      await this.commonService.postRequest('forms/publish/submitForm', model);
      this.toastMessagesService.toastMessages('Η υποβολή της δήλωσής σας ολοκληρώθηκε με επιτυχία');
      if (this.isExternal) {
        await this.router.navigate(['success']);
      } else {
        await this.router.navigate(['home']);
      }
    } catch (e) {
      console.log(e);
      this.toastMessagesService.toastErrorMessage('Δεν μπορέσαμε να ενημερώσουμε την δήλωσή σας. Παρακαλούμε δοκιμάστε ξανά');
    } finally {
      this.isLoading = false;
    }
  }

  editorCreated($event: any, value) {
    $event.root.innerHTML = value;
  }

}
